import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { MagnifyingGlass, UserPlus, MapPin, GraduationCap } from "@phosphor-icons/react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";

export default function Directory() {
  const [q, setQ] = useState("");
  const [location, setLocation] = useState("");
  const [data, setData] = useState({ total: 0, results: [] });
  const [loading, setLoading] = useState(true);

  const search = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/alumni", { params: { q, location } });
      setData(data);
    } finally {
      setLoading(false);
    }
  }, [q, location]);

  useEffect(() => {
    const t = setTimeout(search, 300);
    return () => clearTimeout(t);
  }, [search]);

  const connect = async (id) => {
    try {
      await api.post("/connections", { addressee_id: id });
      toast.success("Connection request sent");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Already connected");
    }
  };

  return (
    <main className="mx-auto max-w-7xl px-5 py-8">
      <p className="overline text-secondary mb-1">Find a classmate</p>
      <h1 className="font-heading text-3xl font-light tracking-tight sm:text-4xl">Alumni Directory</h1>
      <p className="mt-2 text-muted-foreground">Search {data.total} alumni across the world — privacy-first.</p>

      <div className="mt-6 flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <MagnifyingGlass className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name, headline or institution…" className="pl-11 rounded-full" data-testid="directory-search" />
        </div>
        <div className="relative sm:w-64">
          <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
          <Input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Location" className="pl-11 rounded-full" data-testid="directory-location" />
        </div>
      </div>

      <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {loading && <p className="text-muted-foreground">Searching…</p>}
        {!loading && data.results.length === 0 && <p className="text-muted-foreground">No alumni found. Try another search.</p>}
        {data.results.map((p, i) => {
          const initials = (p.name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("");
          const inst = p.education?.[0]?.institution;
          return (
            <Card key={p.user_id} className="group border-border/60 p-6 shadow-sm transition-colors hover:border-secondary/40 animate-fade-up" style={{ animationDelay: `${i * 0.03}s` }} data-testid="directory-card">
              <div className="flex items-start gap-4">
                <Avatar className="h-14 w-14"><AvatarImage src={fileUrl(p.avatar_file_id)} /><AvatarFallback className="bg-accent">{initials}</AvatarFallback></Avatar>
                <div className="min-w-0 flex-1">
                  <Link to={`/u/${p.public_slug || p.user_id}`} className="block truncate font-heading text-lg font-medium hover:underline">{p.name}</Link>
                  <p className="truncate text-sm text-muted-foreground">{p.headline || "Alumnus"}</p>
                  {p.location && <p className="mt-1 flex items-center gap-1 text-xs text-muted-foreground"><MapPin size={13} /> {p.location}</p>}
                </div>
              </div>
              {inst && <Badge variant="secondary" className="mt-4 gap-1 rounded-full font-normal"><GraduationCap size={13} /> {inst}</Badge>}
              <div className="mt-5 flex gap-2">
                <Link to={`/u/${p.public_slug || p.user_id}`} className="flex-1"><Button variant="outline" size="sm" className="w-full rounded-full">View</Button></Link>
                <Button size="sm" className="flex-1 rounded-full gap-1.5" onClick={() => connect(p.user_id)} data-testid="directory-connect"><UserPlus size={15} /> Connect</Button>
              </div>
            </Card>
          );
        })}
      </div>
    </main>
  );
}
